import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckIcon from '@mui/icons-material/Check';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import NotesIcon from '@mui/icons-material/Notes';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Tooltip, PseudoLink2, useCopyText } from '../../primitives';
import { useReorderItemsByIndex, ReorderItemsDialog } from '../../primitives/ReorderItems';
import { CalculatorConfig, InputDefinition, RuleDefinition, OutputFieldDefinition, InputFieldType, SelectOption } from '../types';

export interface CalculatorEditorProps {
  config: CalculatorConfig;
  onChange?: (config: CalculatorConfig) => void;
  onSave?: (config: CalculatorConfig) => void;
  columns?: boolean;
  noTitle?: boolean;
  columnBorders?: boolean;
}

const formatOptionsToText = (options?: SelectOption[]): string => {
  if (!options || !Array.isArray(options)) return '';
  return options
    .map((o) => {
      const parts: (string | number)[] = [];
      const hasDistinctLabel = o.label !== undefined && o.label !== '' && o.label !== String(o.value);
      if (hasDistinctLabel) {
        parts.push(o.label);
        parts.push(o.value ?? '');
      } else {
        parts.push(o.value ?? o.label ?? '');
      }
      if (o.extraValues && Array.isArray(o.extraValues) && o.extraValues.length > 0) {
        parts.push(...o.extraValues);
      }
      return parts.join(' : ');
    })
    .join('\n');
};

const parseOptionsFromText = (text: string): SelectOption[] => {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const rawParts = line.split(':').map((p) => p.trim());
      const parsedParts = rawParts.map((part) => {
        const num = Number(part);
        return !isNaN(num) && part !== '' ? num : part;
      });

      if (parsedParts.length === 1) {
        return { label: rawParts[0], value: parsedParts[0] };
      }
      const label = rawParts[0];
      const value = parsedParts[1];
      const extraValues = parsedParts.slice(2);
      return {
        label: label || String(value),
        value,
        ...(extraValues.length > 0 ? { extraValues } : {}),
      };
    });
};

interface InputDefinitionEditorProps {
  input: InputDefinition;
  index: number;
  onUpdate: (updated: InputDefinition) => void;
  onDelete: () => void;
}

interface RuleDefinitionEditorProps {
  rule: RuleDefinition;
  index: number;
  onUpdate: (updated: RuleDefinition) => void;
  onDelete: () => void;
}

const RuleDefinitionEditor: React.FC<RuleDefinitionEditorProps> = ({
  rule,
  index,
  onUpdate,
  onDelete,
}) => {
  const [showCondition, setShowCondition] = useState(() => Boolean(rule.condition && rule.condition.trim()));
  const [showPrecision, setShowPrecision] = useState(() => rule.precision !== undefined && rule.precision !== null && (rule.precision as any) !== '');

  const hasCondition = Boolean(rule.condition && rule.condition.trim());
  const isConditionVisible = showCondition || hasCondition;

  const hasPrecision = rule.precision !== undefined && rule.precision !== null && (rule.precision as any) !== '';
  const isPrecisionVisible = showPrecision || hasPrecision;

  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {rule.name || `Правило ${index + 1}`} (→ {rule.targetField})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Название правила"
            size="small"
            fullWidth
            value={rule.name || ''}
            onChange={(e) => {
              onUpdate({ ...rule, name: e.target.value });
            }}
          />
          <TextField
            label="Целевое поле"
            size="small"
            fullWidth
            value={rule.targetField || ''}
            onChange={(e) => {
              onUpdate({ ...rule, targetField: e.target.value });
            }}
          />
        </Box>

        {isConditionVisible && (
          <TextField
            label="Условие (например: hours > 40)"
            size="small"
            fullWidth
            multiline
            value={rule.condition || ''}
            onChange={(e) => {
              onUpdate({ ...rule, condition: e.target.value });
            }}
          />
        )}

        <TextField
          label="Формула (например: hours * rate)"
          size="small"
          fullWidth
          multiline
          value={rule.formula || ''}
          onChange={(e) => {
            onUpdate({ ...rule, formula: e.target.value });
          }}
        />

        {isPrecisionVisible && (
          <TextField
            label="Точность округления (знаков после запятой)"
            type="number"
            size="small"
            fullWidth
            inputProps={{ min: 0, max: 20, step: 1 }}
            value={rule.precision ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                onUpdate({ ...rule, precision: undefined });
              } else {
                const num = parseInt(raw, 10);
                onUpdate({ ...rule, precision: Number.isNaN(num) ? undefined : num });
              }
            }}
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isConditionVisible && (
              <Button
                size="small"
                variant="text"
                sx={{ textTransform: 'none', px: 0, minWidth: 'auto' }}
                onClick={() => setShowCondition(true)}
              >
                + условие
              </Button>
            )}
            {!isPrecisionVisible && (
              <Button
                size="small"
                variant="text"
                sx={{ textTransform: 'none', px: 0, minWidth: 'auto' }}
                onClick={() => setShowPrecision(true)}
              >
                + округление
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip text="Удалить правило">
              <IconButton color="error" size="small" onClick={onDelete}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip text={rule.enabled !== false ? 'Отключить правило' : 'Включить правило'}>
              <Switch
                size="small"
                checked={rule.enabled !== false}
                onChange={(e) => {
                  onUpdate({ ...rule, enabled: e.target.checked });
                }}
              />
            </Tooltip>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const InputDefinitionEditor: React.FC<InputDefinitionEditorProps> = ({
  input,
  index,
  onUpdate,
  onDelete,
}) => {
  const [optionsText, setOptionsText] = useState(() => formatOptionsToText(input.options));
  const [showDescription, setShowDescription] = useState(() => Boolean(input.description && input.description.trim()));

  const hasDescription = Boolean(input.description && input.description.trim());
  const isDescriptionVisible = showDescription || hasDescription;

  useEffect(() => {
    const currentParsed = parseOptionsFromText(optionsText);
    if (JSON.stringify(currentParsed) !== JSON.stringify(input.options)) {
      setOptionsText(formatOptionsToText(input.options));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.options]);

  const handleOptionsChange = (newText: string) => {
    setOptionsText(newText);
    const parsed = parseOptionsFromText(newText);
    onUpdate({ ...input, options: parsed });
  };

  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {input.label || input.name || `Параметр ${index + 1}`} ({input.name}) [{input.type}]
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="ID"
            size="small"
            fullWidth
            value={input.name || ''}
            onChange={(e) => {
              const val = e.target.value;
              onUpdate({ ...input, name: val, id: input.id || val });
            }}
          />
          <TextField
            label="Метка"
            size="small"
            fullWidth
            value={input.label || ''}
            onChange={(e) => {
              const val = e.target.value;
              onUpdate({ ...input, label: val });
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Тип</InputLabel>
            <Select
              value={input.type || 'number'}
              label="Тип"
              onChange={(e) => {
                const val = e.target.value as InputFieldType;
                onUpdate({ ...input, type: val });
              }}
            >
              <MenuItem value="number">Число</MenuItem>
              <MenuItem value="text">Текст</MenuItem>
              <MenuItem value="boolean">Переключатель</MenuItem>
              <MenuItem value="select">Выпадающий список</MenuItem>
              <MenuItem value="separator">Разделитель</MenuItem>
            </Select>
          </FormControl>

          {input.type !== 'separator' && (
            <TextField
              label="Значение по умолчанию"
              size="small"
              fullWidth
              value={input.defaultValue ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (input.type === 'number') {
                  const num = Number(raw);
                  const val = raw === '' ? '' : (!isNaN(num) && !raw.endsWith('.') && raw !== '-' ? num : raw);
                  onUpdate({ ...input, defaultValue: val });
                } else if (input.type === 'select') {
                  const num = Number(raw);
                  const val = !isNaN(num) && raw.trim() !== '' && !raw.endsWith('.') ? num : raw;
                  onUpdate({ ...input, defaultValue: val });
                } else {
                  onUpdate({ ...input, defaultValue: raw });
                }
              }}
            />
          )}
        </Box>

        {input.type === 'select' && (
          <TextField
            label="Варианты выбора"
            size="small"
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            value={optionsText}
            onChange={(e) => handleOptionsChange(e.target.value)}
            helperText="Формат: Название : значение : значение_2 : значение_3 (например: Small : s : 1.0 : 2000 создаст также <ID>_val1 и <ID>_val2)"
          />
        )}

        {isDescriptionVisible && (
          <TextField
            label="Описание"
            size="small"
            fullWidth
            multiline
            value={input.description || ''}
            onChange={(e) => onUpdate({ ...input, description: e.target.value })}
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {!isDescriptionVisible && (
              <Button
                size="small"
                variant="text"
                sx={{ textTransform: 'none', px: 0, minWidth: 'auto' }}
                onClick={() => setShowDescription(true)}
              >
                + описание
              </Button>
            )}
            <Button
              size="small"
              variant="text"
              sx={{ textTransform: 'none', px: 0, minWidth: 'auto' }}
              onClick={() => {
                const next = !input.optional;
                onUpdate({ ...input, optional: next });
              }}
            >
              {input.optional ? '+ обязательное' : '+ опциональное'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip text="Удалить параметр">
              <IconButton color="error" size="small" onClick={onDelete}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const CalculatorEditor: React.FC<CalculatorEditorProps> = ({
  config,
  onChange,
  onSave,
  columns = false,
  noTitle = false,
  columnBorders = false,
}) => {
  const [isTableView, setIsTableView] = useState(false);
  const [isInputsTableView, setIsInputsTableView] = useState(false);
  const [rulePopupState, setRulePopupState] = useState<{
    type: 'condition' | 'precision';
    index: number;
    anchorEl: HTMLElement | null;
  } | null>(null);
  const [inputDescPopupState, setInputDescPopupState] = useState<{
    index: number;
    anchorEl: HTMLElement | null;
  } | null>(null);
  const copyText = useCopyText();

  const updateConfig = (updater: (prev: CalculatorConfig) => CalculatorConfig) => {
    if (onChange) {
      onChange(updater(config));
    }
  };

  const reorderInputsLogic = useReorderItemsByIndex<InputDefinition>({
    updateItems: async (newInputs) => {
      updateConfig((prev) => ({ ...prev, inputs: newInputs }));
      return Promise.resolve();
    },
  });

  const reorderRulesLogic = useReorderItemsByIndex<RuleDefinition>({
    updateItems: async (newRules) => {
      updateConfig((prev) => ({ ...prev, rules: newRules }));
      return Promise.resolve();
    },
  });

  // Extract all target fields from rules
  const rawTargetFieldsFromRules = Array.from(
    new Set((config.rules || []).map((r) => r.targetField).filter(Boolean))
  );

  // Order target fields by existing config.outputs order, followed by any new/unconfigured fields
  const outputsOrder = (config.outputs || []).map((o) => o.field);
  const targetFieldsFromRules = [...rawTargetFieldsFromRules].sort((a, b) => {
    const indexA = outputsOrder.indexOf(a);
    const indexB = outputsOrder.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  // Normalize outputs list for reordering so every targetField has an item
  const getNormalizedOutputs = (): OutputFieldDefinition[] => {
    return targetFieldsFromRules.map((field) => {
      const existing = (config.outputs || []).find((o) => o.field === field);
      const ruleForField = (config.rules || []).find((r) => r.targetField === field);
      return (
        existing || {
          field,
          name: ruleForField?.name || field,
          isResult: true,
          isIntermediary: false,
          is_hidden: false,
        }
      );
    });
  };

  const reorderOutputsLogic = useReorderItemsByIndex<OutputFieldDefinition>({
    updateItems: async (newOutputs) => {
      updateConfig((prev) => ({ ...prev, outputs: newOutputs }));
      return Promise.resolve();
    },
  });

  const addInput = () => {
    const newId = `input_${Date.now()}`;
    const newInput: InputDefinition = {
      id: newId,
      name: `param_${(config.inputs?.length || 0) + 1}`,
      label: 'Новый параметр',
      type: 'number',
      defaultValue: 0,
    };
    updateConfig((prev) => ({ ...prev, inputs: [...(prev.inputs || []), newInput] }));
  };

  const addRule = () => {
    const newId = `rule_${Date.now()}`;
    const newRule: RuleDefinition = {
      id: newId,
      name: 'Новое правило',
      formula: '0',
      targetField: `result_${(config.rules?.length || 0) + 1}`,
      enabled: true,
    };
    updateConfig((prev) => ({ ...prev, rules: [...(prev.rules || []), newRule] }));
  };

  // Section 1: Title, Description, Outputs
  const renderMetaAndOutputs = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {!noTitle && <Typography variant="h6">Конфигурация калькулятора</Typography>}

      {/* Basic Info */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Название калькулятора"
          size="small"
          fullWidth
          value={config.title || ''}
          onChange={(e) => updateConfig((prev) => ({ ...prev, title: e.target.value }))}
        />
        <TextField
          label="Описание"
          size="small"
          fullWidth
          multiline
          value={config.description || ''}
          onChange={(e) => updateConfig((prev) => ({ ...prev, description: e.target.value }))}
        />
        <TextField
          label="Дополнительное описание"
          size="small"
          fullWidth
          multiline
          value={config.description_more || ''}
          onChange={(e) => updateConfig((prev) => ({ ...prev, description_more: e.target.value }))}
        />
      </Box>

      <Divider />

      {/* Output Fields Configuration */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Конфигурация полей вывода (опционально)
          </Typography>
          {targetFieldsFromRules.length > 1 && (
            <IconButton
              size="small"
              onClick={() => {
                reorderOutputsLogic.startEditing(getNormalizedOutputs());
              }}
              title="Изменить порядок полей вывода"
            >
              <SwapVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          Настройте отображение, названия и статус результатов для полей, вычисляемых правилами.
        </Typography>

        {targetFieldsFromRules.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Добавьте правила с целевыми полями (targetField), чтобы настроить их вывод.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'rgba(0, 0, 0, 0.35)',
                      lineHeight: 1,
                      pt: 1,
                      pb: 1,
                      width: '28%',
                    }}
                  >
                    Поле
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'rgba(0, 0, 0, 0.35)',
                      lineHeight: 1,
                      pt: 1,
                      pb: 1,
                    }}
                  >
                    Название
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      pt: 1,
                      pb: 1,
                      width: 120,
                    }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {targetFieldsFromRules.map((field) => {
                  const existingOutput = (config.outputs || []).find((o) => o.field === field);
                  const ruleForField = (config.rules || []).find((r) => r.targetField === field);
                  const displayName = existingOutput ? existingOutput.name : ruleForField?.name || field;
                  const isResult = existingOutput ? Boolean(existingOutput.isResult) : true;
                  const isIntermediary = existingOutput ? Boolean(existingOutput.isIntermediary) : false;
                  const isHidden = existingOutput ? Boolean(existingOutput.is_hidden) : false;

                  return (
                    <TableRow key={field} hover>
                      <TableCell sx={{ verticalAlign: 'middle', py: 0.75 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {field}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle', py: 0.75 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={displayName}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateConfig((prev) => {
                              const outputs = [...(prev.outputs || [])];
                              const idx = outputs.findIndex((o) => o.field === field);
                              if (idx >= 0) {
                                outputs[idx] = { ...outputs[idx], name: val };
                              } else {
                                outputs.push({ field, name: val, isResult, isIntermediary, is_hidden: isHidden });
                              }
                              return { ...prev, outputs };
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: 'middle', py: 0.75 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip text={isHidden ? 'Скрыто (не показывать в выводе)' : 'Скрыть поле из вывода'}>
                            <IconButton
                              size="small"
                              color={isHidden ? 'primary' : 'default'}
                              onClick={() => {
                                updateConfig((prev) => {
                                  const outputs = [...(prev.outputs || [])];
                                  const idx = outputs.findIndex((o) => o.field === field);
                                  const nextVal = !isHidden;
                                  if (idx >= 0) {
                                    outputs[idx] = { ...outputs[idx], is_hidden: nextVal };
                                  } else {
                                    outputs.push({ field, name: displayName, isResult, isIntermediary, is_hidden: nextVal });
                                  }
                                  return { ...prev, outputs };
                                });
                              }}
                            >
                              <VisibilityOffOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip text={isIntermediary ? 'Промежуточное поле' : 'Сделать промежуточным'}>
                            <IconButton
                              size="small"
                              color={isIntermediary ? 'primary' : 'default'}
                              onClick={() => {
                                updateConfig((prev) => {
                                  const outputs = [...(prev.outputs || [])];
                                  const idx = outputs.findIndex((o) => o.field === field);
                                  const nextVal = !isIntermediary;
                                  if (idx >= 0) {
                                    outputs[idx] = { ...outputs[idx], isIntermediary: nextVal };
                                  } else {
                                    outputs.push({ field, name: displayName, isResult, isIntermediary: nextVal, is_hidden: isHidden });
                                  }
                                  return { ...prev, outputs };
                                });
                              }}
                            >
                              <MoreHorizIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip text={isResult ? 'Результат (главный)' : 'Показывать как результат'}>
                            <IconButton
                              size="small"
                              color={isResult ? 'primary' : 'default'}
                              onClick={() => {
                                updateConfig((prev) => {
                                  const outputs = [...(prev.outputs || [])];
                                  const idx = outputs.findIndex((o) => o.field === field);
                                  const nextVal = !isResult;
                                  if (idx >= 0) {
                                    outputs[idx] = { ...outputs[idx], isResult: nextVal };
                                  } else {
                                    outputs.push({ field, name: displayName, isResult: nextVal, isIntermediary, is_hidden: isHidden });
                                  }
                                  return { ...prev, outputs };
                                });
                              }}
                            >
                              <EmojiEventsOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {onSave && (
        <Button variant="contained" onClick={() => onSave(config)}>
          Сохранить конфигурацию
        </Button>
      )}
    </Box>
  );

  const inputsCount = config.inputs?.length || 0;
  const rulesCount = config.rules?.length || 0;

  // Section 2: Inputs
  const renderInputs = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Входные параметры ({inputsCount})
          </Typography>
          <IconButton size="small" onClick={() => setIsInputsTableView(true)}>
            <EditNoteIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title="Изменить порядок параметров"
            onClick={() => reorderInputsLogic.startEditing(config.inputs || [])}
          >
            <SwapVertIcon fontSize="small" />
          </IconButton>
        </Box>
        {inputsCount === 0 && (
          <Button size="small" startIcon={<AddIcon />} color="primary" onClick={addInput}>
            параметр
          </Button>
        )}
      </Box>

      {(config.inputs || []).map((input, index) => (
        <InputDefinitionEditor
          key={input.id || index}
          input={input}
          index={index}
          onUpdate={(updated) => {
            updateConfig((prev) => {
              const inputs = [...prev.inputs];
              inputs[index] = updated;
              return { ...prev, inputs };
            });
          }}
          onDelete={() => {
            updateConfig((prev) => ({
              ...prev,
              inputs: prev.inputs.filter((_, i) => i !== index),
            }));
          }}
        />
      ))}

      {inputsCount > 0 && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          color="primary"
          sx={{ alignSelf: 'flex-end', mt: 1 }}
          onClick={addInput}
        >
          параметр
        </Button>
      )}
    </Box>
  );

  const renderInputsTableMode = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden', p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Входные параметры
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" startIcon={<AddIcon />} color="primary" onClick={addInput}>
              параметр
            </Button>
            <Tooltip text="Вернуться к главному виду">
              <IconButton
                onClick={() => setIsInputsTableView(false)}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                  boxShadow: 2,
                }}
                size="small"
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, maxHeight: 'calc(100vh - 120px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140, fontWeight: 600, fontSize: '0.75rem', py: 1 }}>ID</TableCell>
                <TableCell sx={{ width: 180, fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Метка</TableCell>
                <TableCell sx={{ width: 140, fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Тип</TableCell>
                <TableCell sx={{ width: 130, fontWeight: 600, fontSize: '0.75rem', py: 1 }}>По умолч.</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <span>Варианты выбора</span>
                    <Tooltip text="Формат: Название : значение : значение_2 : значение_3 (например: Small : s : 1.0 : 2000 создаст также <ID>_val1 и <ID>_val2)">
                      <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'pointer' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ width: 90, fontWeight: 600, fontSize: '0.75rem', py: 1 }} align="center">Настр.</TableCell>
                <TableCell sx={{ width: 50, py: 1 }} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(config.inputs || []).map((input, index) => {
                const hasDesc = Boolean(input.description && input.description.trim());
                const isSelect = input.type === 'select';
                const isSeparator = input.type === 'separator';

                return (
                  <TableRow key={input.id || index} hover sx={{ verticalAlign: 'top' }}>
                    <TableCell sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={input.name || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const inputs = [...(config.inputs || [])];
                            inputs[index] = { ...inputs[index], name: val, id: inputs[index].id || val };
                            updateConfig((prev) => ({ ...prev, inputs }));
                          }}
                        />
                        <IconButton size="small" onClick={() => copyText(input.name || '')}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={input.label || ''}
                        onChange={(e) => {
                          const inputs = [...(config.inputs || [])];
                          inputs[index] = { ...inputs[index], label: e.target.value };
                          updateConfig((prev) => ({ ...prev, inputs }));
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={input.type || 'number'}
                          onChange={(e) => {
                            const val = e.target.value as InputFieldType;
                            const inputs = [...(config.inputs || [])];
                            inputs[index] = { ...inputs[index], type: val };
                            updateConfig((prev) => ({ ...prev, inputs }));
                          }}
                        >
                          <MenuItem value="number">Число</MenuItem>
                          <MenuItem value="text">Текст</MenuItem>
                          <MenuItem value="boolean">Переключатель</MenuItem>
                          <MenuItem value="select">Выпадающий список</MenuItem>
                          <MenuItem value="separator">Разделитель</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {!isSeparator && (
                        <TextField
                          size="small"
                          fullWidth
                          value={input.defaultValue ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            let val: any = raw;
                            if (input.type === 'number') {
                              const num = Number(raw);
                              val = raw === '' ? '' : (!isNaN(num) && !raw.endsWith('.') && raw !== '-' ? num : raw);
                            } else if (input.type === 'select') {
                              const num = Number(raw);
                              val = !isNaN(num) && raw.trim() !== '' && !raw.endsWith('.') ? num : raw;
                            }
                            const inputs = [...(config.inputs || [])];
                            inputs[index] = { ...inputs[index], defaultValue: val };
                            updateConfig((prev) => ({ ...prev, inputs }));
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {isSelect ? (
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={4}
                          value={formatOptionsToText(input.options)}
                          onChange={(e) => {
                            const parsed = parseOptionsFromText(e.target.value);
                            const inputs = [...(config.inputs || [])];
                            inputs[index] = { ...inputs[index], options: parsed };
                            updateConfig((prev) => ({ ...prev, inputs }));
                          }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1 }} align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                        <Tooltip text={input.optional ? 'Опциональное поле (клик - сделать обязательным)' : 'Обязательное поле (клик - сделать опциональным)'}>
                          <IconButton
                            size="small"
                            color={input.optional ? 'primary' : 'default'}
                            onClick={() => {
                              const inputs = [...(config.inputs || [])];
                              inputs[index] = { ...inputs[index], optional: !input.optional };
                              updateConfig((prev) => ({ ...prev, inputs }));
                            }}
                          >
                            {input.optional ? <CheckCircleOutlineIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip text="Описание">
                          <IconButton
                            size="small"
                            color={hasDesc ? 'primary' : 'default'}
                            onClick={(e) => setInputDescPopupState({ index, anchorEl: e.currentTarget })}
                          >
                            <NotesIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1, whiteSpace: 'nowrap' }} align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          updateConfig((prev) => ({
                            ...prev,
                            inputs: prev.inputs.filter((_, i) => i !== index),
                          }));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Popover
          open={Boolean(inputDescPopupState?.anchorEl)}
          anchorEl={inputDescPopupState?.anchorEl}
          onClose={() => setInputDescPopupState(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          PaperProps={{ sx: { p: 2, width: 340 } }}
        >
          {inputDescPopupState && (() => {
            const input = (config.inputs || [])[inputDescPopupState.index];
            if (!input) return null;

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Описание параметра</Typography>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Дополнительные пояснения для пользователя..."
                  value={input.description || ''}
                  onChange={(e) => {
                    const inputs = [...(config.inputs || [])];
                    inputs[inputDescPopupState.index] = { ...inputs[inputDescPopupState.index], description: e.target.value };
                    updateConfig((prev) => ({ ...prev, inputs }));
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      const inputs = [...(config.inputs || [])];
                      inputs[inputDescPopupState.index] = { ...inputs[inputDescPopupState.index], description: undefined };
                      updateConfig((prev) => ({ ...prev, inputs }));
                      setInputDescPopupState(null);
                    }}
                  >
                    Сбросить
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<CheckIcon fontSize="small" />}
                    onClick={() => setInputDescPopupState(null)}
                  >
                    Готово
                  </Button>
                </Box>
              </Box>
            );
          })()}
        </Popover>
      </Box>
    );
  };

  // Section 3: Rules
  const renderRulesTableMode = () => {
    return (
      <Box sx={{ display: 'flex', height: '100%', width: '100%', overflow: 'hidden', position: 'relative' }}>
        <Box sx={{ width: 220, flexShrink: 0, p: 2, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto' }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Параметры
          </Typography>
          {(config.inputs || []).map((input) => (
            <Box key={input.id || input.name} sx={{ mb: 1 }}>
              <PseudoLink2
                onClick={() => copyText(input.name)}
                style={{ cursor: 'pointer', display: 'inline-block', fontSize: '0.8rem', lineHeight: 1.3 }}
                title="Нажмите, чтобы скопировать ID"
              >
                {input.label || input.name} <span style={{ opacity: 0.6, fontFamily: 'monospace' }}>({input.name})</span>
              </PseudoLink2>
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Формулы
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" startIcon={<AddIcon />} color="primary" onClick={addRule}>
                правило
              </Button>
              <Tooltip text="Вернуться к главному виду">
                <IconButton
                  onClick={() => setIsTableView(false)}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' },
                    boxShadow: 2,
                  }}
                  size="small"
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 120px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '20%', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Название</TableCell>
                  <TableCell sx={{ width: '20%', fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Цель</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Формула</TableCell>
                  <TableCell sx={{ width: 120, fontWeight: 600, fontSize: '0.75rem', py: 1 }} align="center">Настр.</TableCell>
                  <TableCell sx={{ width: 80, py: 1 }} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {(config.rules || []).map((rule, index) => {
                  const hasCondition = rule.condition !== undefined && rule.condition !== null && rule.condition !== '';
                  const hasPrecision = rule.precision !== undefined && rule.precision !== null && (rule.precision as any) !== '';

                  return (
                    <TableRow key={rule.id || index} hover sx={{ verticalAlign: 'top' }}>
                      <TableCell sx={{ py: 1 }}>
                        <TextField size="small" fullWidth value={rule.name || ''} onChange={(e) => {
                          const rules = [...(config.rules || [])];
                          rules[index] = { ...rules[index], name: e.target.value };
                          updateConfig(prev => ({ ...prev, rules }));
                        }} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField size="small" fullWidth value={rule.targetField || ''} onChange={(e) => {
                            const rules = [...(config.rules || [])];
                            rules[index] = { ...rules[index], targetField: e.target.value };
                            updateConfig(prev => ({ ...prev, rules }));
                          }} />
                          <IconButton size="small" onClick={() => copyText(rule.targetField || '')}><ContentCopyIcon fontSize="small" /></IconButton>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                          <TextField size="small" fullWidth multiline value={rule.formula || ''} onChange={(e) => {
                            const rules = [...(config.rules || [])];
                            rules[index] = { ...rules[index], formula: e.target.value };
                            updateConfig(prev => ({ ...prev, rules }));
                          }} />
                      </TableCell>
                      <TableCell sx={{ py: 1 }} align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                              <Tooltip text={`Точность ${hasPrecision ? rule.precision : ''}`}>
                                  <IconButton
                                    size="small"
                                    color={hasPrecision ? 'primary' : 'default'}
                                    onClick={(e) => setRulePopupState({ type: 'precision', index, anchorEl: e.currentTarget })}
                                  >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                          <TuneIcon fontSize="small" />
                                          {hasPrecision && <Typography variant="caption" sx={{ fontWeight: 600 }}>{rule.precision}</Typography>}
                                      </Box>
                                  </IconButton>
                              </Tooltip>
                              <Tooltip text="Условие">
                                  <IconButton
                                    size="small"
                                    color={hasCondition ? 'primary' : 'default'}
                                    onClick={(e) => setRulePopupState({ type: 'condition', index, anchorEl: e.currentTarget })}
                                  >
                                    <FilterListIcon fontSize="small" />
                                  </IconButton>
                              </Tooltip>
                          </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1, whiteSpace: 'nowrap' }} align="right">
                         <Switch size="small" checked={rule.enabled !== false} onChange={(e) => {
                            const rules = [...(config.rules || [])];
                            rules[index] = { ...rules[index], enabled: e.target.checked };
                            updateConfig(prev => ({ ...prev, rules }));
                         }} />
                         <IconButton size="small" onClick={() => {
                            updateConfig(prev => ({ ...prev, rules: prev.rules.filter((_, i) => i !== index) }));
                         }}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Popover
          open={Boolean(rulePopupState?.anchorEl)}
          anchorEl={rulePopupState?.anchorEl}
          onClose={() => setRulePopupState(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          PaperProps={{ sx: { p: 2, width: 320 } }}
        >
          {rulePopupState && (() => {
            const rule = (config.rules || [])[rulePopupState.index];
            if (!rule) return null;

            if (rulePopupState.type === 'condition') {
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Условие правила</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Например: age > 18"
                    value={rule.condition || ''}
                    onChange={(e) => {
                      const rules = [...(config.rules || [])];
                      rules[rulePopupState.index] = { ...rules[rulePopupState.index], condition: e.target.value };
                      updateConfig(prev => ({ ...prev, rules }));
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => {
                        const rules = [...(config.rules || [])];
                        rules[rulePopupState.index] = { ...rules[rulePopupState.index], condition: undefined };
                        updateConfig(prev => ({ ...prev, rules }));
                        setRulePopupState(null);
                      }}
                    >
                      Сбросить
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<CheckIcon fontSize="small" />}
                      onClick={() => setRulePopupState(null)}
                    >
                      Готово
                    </Button>
                  </Box>
                </Box>
              );
            }

            if (rulePopupState.type === 'precision') {
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Точность округления</Typography>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    placeholder="Знаков после запятой (например: 2)"
                    value={rule.precision !== undefined && rule.precision !== null ? rule.precision : ''}
                    onChange={(e) => {
                      const rules = [...(config.rules || [])];
                      const val = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                      rules[rulePopupState.index] = { ...rules[rulePopupState.index], precision: isNaN(val as number) ? undefined : val };
                      updateConfig(prev => ({ ...prev, rules }));
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => {
                        const rules = [...(config.rules || [])];
                        rules[rulePopupState.index] = { ...rules[rulePopupState.index], precision: undefined };
                        updateConfig(prev => ({ ...prev, rules }));
                        setRulePopupState(null);
                      }}
                    >
                      Сбросить
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<CheckIcon fontSize="small" />}
                      onClick={() => setRulePopupState(null)}
                    >
                      Готово
                    </Button>
                  </Box>
                </Box>
              );
            }

            return null;
          })()}
        </Popover>
      </Box>
    );
  };

  const renderRules = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Правила и формулы ({rulesCount})
            </Typography>
            <IconButton size="small" onClick={() => setIsTableView(!isTableView)}>
               <EditNoteIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Изменить порядок правил"
              onClick={() => reorderRulesLogic.startEditing(config.rules || [])}
            >
              <SwapVertIcon fontSize="small" />
            </IconButton>
          </Box>
          {rulesCount === 0 && (
            <Button size="small" startIcon={<AddIcon />} color="primary" onClick={addRule}>
              правило
            </Button>
          )}
        </Box>

        {(config.rules || []).map((rule, index) => (
          <RuleDefinitionEditor
            key={rule.id || index}
            rule={rule}
            index={index}
            onUpdate={(updated) => {
              updateConfig((prev) => {
                const rules = [...prev.rules];
                rules[index] = updated;
                return { ...prev, rules };
              });
            }}
            onDelete={() => {
              updateConfig((prev) => ({
                ...prev,
                rules: prev.rules.filter((_, i) => i !== index),
              }));
            }}
          />
        ))}

        {rulesCount > 0 && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            color="primary"
            sx={{ alignSelf: 'flex-end', mt: 1 }}
            onClick={addRule}
          >
            правило
          </Button>
        )}
      </Box>
    );
  };

  const reorderDialogs = (
    <>
      {reorderInputsLogic.isEditing && reorderInputsLogic.items && (
        <ReorderItemsDialog<InputDefinition>
          title="Изменить порядок параметров"
          data={reorderInputsLogic}
          ItemComponent={({ item }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.label || item.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ({item.name}, {item.type})
              </Typography>
            </Box>
          )}
          itemKey={(item) => item.id || item.name}
        />
      )}

      {reorderRulesLogic.isEditing && reorderRulesLogic.items && (
        <ReorderItemsDialog<RuleDefinition>
          title="Изменить порядок правил"
          data={reorderRulesLogic}
          ItemComponent={({ item }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.name || item.id}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                (→ {item.targetField})
              </Typography>
            </Box>
          )}
          itemKey={(item) => item.id || item.targetField}
        />
      )}

      {reorderOutputsLogic.isEditing && reorderOutputsLogic.items && (
        <ReorderItemsDialog<OutputFieldDefinition>
          title="Изменить порядок полей вывода"
          data={reorderOutputsLogic}
          ItemComponent={({ item }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.name || item.field}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                ({item.field})
              </Typography>
            </Box>
          )}
          itemKey={(item) => item.field}
        />
      )}
    </>
  );

  if (isTableView) {
    return (
      <>
        {renderRulesTableMode()}
        {reorderDialogs}
      </>
    );
  }

  if (isInputsTableView) {
    return (
      <>
        {renderInputsTableMode()}
        {reorderDialogs}
      </>
    );
  }

  if (columns) {
    return (
      <>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: columnBorders ? 0 : 2,
            height: '100%',
            overflow: 'hidden',
            p: columnBorders ? 0 : 2,
          }}
        >
          <Box
            sx={{
              overflowY: 'auto',
              p: columnBorders ? 2 : 0,
              pr: columnBorders ? 2 : 1,
              height: '100%',
              borderRight: columnBorders ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            {renderMetaAndOutputs()}
          </Box>
          <Box
            sx={{
              overflowY: 'auto',
              p: columnBorders ? 2 : 0,
              px: columnBorders ? 2 : 0,
              pr: columnBorders ? 2 : 1,
              height: '100%',
              borderRight: columnBorders ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            {renderInputs()}
          </Box>
          <Box
            sx={{
              overflowY: 'auto',
              p: columnBorders ? 2 : 0,
              pl: columnBorders ? 2 : 0,
              pr: columnBorders ? 2 : 1,
              height: '100%',
            }}
          >
            {renderRules()}
          </Box>
        </Box>
        {reorderDialogs}
      </>
    );
  }

  return (
    <>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {renderMetaAndOutputs()}
        <Divider />
        {renderInputs()}
        <Divider />
        {renderRules()}
      </Box>
      {reorderDialogs}
    </>
  );
};

export default CalculatorEditor;
